import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { getObraStats, workforceCount } from "@/services/obraStats";
import { parseYmdLocal, formatYmd } from "@/utils/dateUtils";

// Linha real: pessoas registradas no workforce de cada RDO da obra
interface AttendanceDetail {
  date: string; // yyyy-mm-dd
  dayOfWeek: string;
  present: number;
  functions: string;
}

interface PeopleHistogramModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Obra cujos RDOs alimentam o histórico */
  obraId?: string;
}

const getTrendIcon = (current: number, previous?: number) => {
  if (previous === undefined) return <Minus className="w-3 h-3 text-gray-400" />;
  if (current > previous) return <TrendingUp className="w-3 h-3 text-green-600" />;
  if (current < previous) return <TrendingDown className="w-3 h-3 text-red-600" />;
  return <Minus className="w-3 h-3 text-gray-400" />;
};

export function PeopleHistogramModal({ open, onOpenChange, obraId }: PeopleHistogramModalProps) {
  const [rows, setRows] = useState<AttendanceDetail[]>([]);

  useEffect(() => {
    if (!open || !obraId) return;
    let active = true;
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        setRows(
          s.rdos.map((rdo) => {
            const funcs = Array.from(
              new Set((rdo.workforce ?? []).map((w) => w.function).filter(Boolean))
            ).join(", ");
            return {
              date: rdo.date,
              dayOfWeek: parseYmdLocal(rdo.date).toLocaleDateString("pt-BR", { weekday: "long" }),
              present: workforceCount(rdo),
              functions: funcs,
            };
          })
        );
      })
      .catch((e) => console.error("PeopleHistogramModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  const totalPresent = rows.reduce((sum, day) => sum + day.present, 0);
  const media = rows.length > 0 ? Math.round(totalPresent / rows.length) : 0;
  const pico = rows.reduce((max, day) => Math.max(max, day.present), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Histórico de Pessoas em Obra
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          {/* Resumo */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{rows.length}</div>
              <div className="text-sm text-muted-foreground">RDOs com registro</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{media}</div>
              <div className="text-sm text-muted-foreground">Média de pessoas/dia</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{pico}</div>
              <div className="text-sm text-muted-foreground">Pico de pessoas</div>
            </div>
          </div>

          {/* Tabela */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Dia da Semana</TableHead>
                  <TableHead className="text-center">Pessoas</TableHead>
                  <TableHead className="text-center">Tendência</TableHead>
                  <TableHead>Funções registradas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Nenhum registro de mão de obra ainda. Preencha o efetivo nos RDOs.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((day, index) => (
                  <TableRow key={day.date} className="hover:bg-muted/30">
                    <TableCell className="font-medium">{formatYmd(day.date)}</TableCell>
                    <TableCell className="capitalize">{day.dayOfWeek}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{day.present}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">
                        {getTrendIcon(day.present, rows[index + 1]?.present)}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {day.functions || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
